<template>
  <div class="relative w-full">
    <RevRadio
      :id
      :aria-describedby="`${id}-description`"
      :aria-label="offer.title"
      :aria-labelledby="`${id}-label`"
      class="bg-static-info-low"
      :class="[$style.radio, { [$style.blueHighlighted]: isBackupPlus }]"
      :model-value="isSelected"
      :tag-props="
        isBackupPlus
          ? {
              variant: 'primary',
              label: i18n(translations.tagLabel),
            }
          : undefined
      "
      :value="id"
      variant="full"
      @click="handleClick"
    >
      <template #label>
        <div :id="`${id}-label`" class="space-y-8">
          <span v-if="isBackupPlus" class="sr-only">{{
            i18n(translations.tagLabel)
          }}</span>

          <span class="body-1-bold">
            {{ offer.title }}
          </span>

          <InsurancePrice
            v-if="offer.price && offer.price.amount !== '0.00'"
            :is-monthly="offer.isMonthly"
            :price="offer.price"
          />
        </div>
      </template>
      <template #description>
        <RevDivider
          class="relative -inset-x-[38px] mb-16 mt-8 w-[calc(100%+38px)]"
        />

        <ul
          :id="`${id}-description`"
          class="relative -inset-x-8 mt-8 space-y-8"
          :class="{ 'text-static-brand-hi': isBackupPlus }"
        >
          <li
            v-for="benefit in offer.benefits.short"
            :key="benefit"
            class="flex items-center"
          >
            <IconCheckSmall class="shrink-0 self-start" /> {{ benefit }}
          </li>
        </ul>
      </template>
    </RevRadio>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { InsuranceOffer } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevDivider } from '@ds/components/Divider'
import { RevRadio } from '@ds/components/Radio'
import { IconCheckSmall } from '@ds/icons/IconCheckSmall'

import InsurancePrice from '../InsurancePrice/InsurancePrice.vue'

import translations from './BackupPlusRadio.translations'

const { trackClick } = useTracking()
const i18n = useI18n()

const props = defineProps<{
  id: string
  offer: InsuranceOffer
  isSelected: boolean
  trackingZone: string
}>()

const emit = defineEmits(['select', 'remove'])
const isBackupPlus = computed(() => props.offer.isBackupPlus)

function handleClick() {
  trackClick({
    name: props.offer.title,
    value: props.offer.isMonthly ? 'monthly' : 'upfront',
    zone: props.trackingZone,
  })

  emit(props.isSelected ? 'remove' : 'select')
}
</script>

<style lang="css" module>
.radio {
  @apply h-full;
}

.radio > label {
  @apply h-full;
  @apply border-l-6;
  @apply border-l-static-default-mid;
}

.radio.blueHighlighted > label {
  @apply border-l-action-brand-hi;
  @apply bg-surface-default-mid;
  @apply hover:bg-static-info-low;
}
</style>
